import React, { useState, useMemo } from 'react';
import {
    Autocomplete,
    TextField,
    AutocompleteProps,
    TextFieldProps
} from '@mui/material';

export interface SearchableSelectOption {
    id: number | string;
    label: string;
    [key: string]: any;
}

interface SearchableSelectProps<T extends SearchableSelectOption> {
    options: T[];
    value?: number | string;
    onChange: (value: number | string | null) => void;
    label?: string;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    name?: string;
    fullWidth?: boolean;
    required?: boolean;
    renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode;
    getOptionLabel?: (option: T) => string;
    noOptionsText?: string;
    loading?: boolean;
}

export function SearchableSelect<T extends SearchableSelectOption>({
    options,
    value,
    onChange,
    label,
    placeholder,
    error = false,
    helperText,
    disabled = false,
    name,
    fullWidth = true,
    required = false,
    renderOption,
    getOptionLabel,
    noOptionsText = 'Nenhuma opção encontrada',
    loading = false
}: SearchableSelectProps<T>) {
    const [inputValue, setInputValue] = useState('');

    const selectedOption = useMemo(() => {
        return options.find(opt => opt.id === value) || null;
    }, [options, value]);

    const defaultGetOptionLabel = (option: T) => option.label;

    return (
        <Autocomplete
            value={selectedOption}
            onChange={(_, newValue) => {
                onChange(newValue ? newValue.id : null);
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            options={options}
            getOptionLabel={getOptionLabel || defaultGetOptionLabel}
            renderOption={renderOption}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    name={name}
                    required={required}
                    sx={{ borderRadius: 2 }}
                />
            )}
            noOptionsText={noOptionsText}
            disabled={disabled}
            fullWidth={fullWidth}
            loading={loading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                }
            }}
        />
    );
}
